import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import LeftMenu from '../../components/LeftMenu';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import {
    FiRefreshCcw,
    FiDownloadCloud,
    FiX,
    FiFileText,
    FiFile,
    FiCalendar,
    FiUser,
    FiChevronDown,
    FiChevronUp,
    FiMapPin,
    FiPhone,
} from 'react-icons/fi';
import {
    GET_SALES_REPORT,
    GET_INVOICE_DETAILS,
    BASE_URL,
    GET_PATIENTS,
} from '../../utlilities/config';
import axios from 'axios';
import { initializeWebUserInfo, getWebUserInfo } from '../../utlilities/webConfig';
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';
import HighlightMetricsModal from './components/HighlightMetricsModal';
import SalesReportBillsTable from './components/SalesReportBillsTable';

const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Loading...</p>
        </div>
    </div>
);

const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatInvoiceForPrint = (invoiceNo, sequence) => {
    const rawString = String(invoiceNo ?? '').trim();
    if (!rawString) return '';
    if (rawString.includes('/')) return rawString;

    const parsedInvoice = Number.parseInt(rawString, 10);
    const invoicePart = Number.isNaN(parsedInvoice)
        ? rawString
        : String(parsedInvoice).padStart(2, '0');

    const finacYear = String(localStorage.getItem('finac_year') || '').trim();
    const storedSeriesName = String(localStorage.getItem('series_name') || '').trim();

    const seqYear = Number.parseInt(sequence, 10);
    if (Number.isNaN(seqYear)) {
        return storedSeriesName ? `${invoicePart}/${storedSeriesName}` : invoicePart;
    }

    const startYY = String(seqYear).slice(-2);
    const endYY = String(seqYear + 1).slice(-2);
    const derivedSeriesName = `${startYY}-${endYY}`;

    const shouldUseStoredSeries = finacYear && String(seqYear) === String(finacYear);
    const seriesNameToUse = shouldUseStoredSeries ? storedSeriesName : derivedSeriesName;

    return seriesNameToUse ? `${invoicePart}/${seriesNameToUse}` : invoicePart;
};

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '36px',
        height: '36px',
        backgroundColor: '#f9fafb',
        borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
        boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
        borderRadius: '0.5rem',
        '&:hover': {
            borderColor: '#6366f1',
        },
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '2px 8px',
        height: '36px',
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '36px',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? '#92c3fcff'
            : state.isFocused
                ? '#92c3fcff'
                : '#ffffff',
        color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
        fontWeight: '500',
        padding: '6px 8px',
        fontSize: '0.85rem',
        '&:hover': {
            backgroundColor: '#92c3fcff',
            color: '#000000ff',
        },
    }),
    singleValue: (provided) => ({
        ...provided,
        fontSize: '0.9rem',
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: '0.9rem',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 999,
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow:
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        zIndex: 9999,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

const customOptionLabel = ({ PatientName, Age, Place, MobileNo, Month, Days }) => {
    const isValidValue = (value) => value !== null && value !== undefined && value !== '';

    return (
        <div className="flex items-center space-x-3 py-1">
            {isValidValue(PatientName) && (
                <div className="font-semibold text-sm text-black">{PatientName}</div>
            )}
            {isValidValue(Age) && (
                <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">{Age} years</div>
            )}
            {isValidValue(Month) && (
                <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">{Month} months</div>
            )}
            {isValidValue(Days) && (
                <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">{Days} days</div>
            )}
            {isValidValue(Place) && (
                <div className="text-xs text-black flex items-center">
                    <FiMapPin className="mr-1" size={12} />
                    {Place}
                </div>
            )}
            {isValidValue(MobileNo) && (
                <div className="text-xs text-black flex items-center">
                    <FiPhone className="mr-1" size={12} />
                    {MobileNo}
                </div>
            )}
        </div>
    );
};

const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                        type="button"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            type="button"
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SalesPatientWise = () => {
    const embedded = useContext(ReportEmbedContext);
    const token = localStorage.getItem('accessToken');

    const [loading, setLoading] = useState(false);
    const [patientSearchLoading, setPatientSearchLoading] = useState(false);
    const [fromDate, setFromDate] = useState(() => formatLocalDate(new Date()));
    const [toDate, setToDate] = useState(() => formatLocalDate(new Date()));
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patientSuggestions, setPatientSuggestions] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [invoiceDetails, setInvoiceDetails] = useState({});
    const [expandedBills, setExpandedBills] = useState(new Set());
    const [loadingDetails, setLoadingDetails] = useState(new Set());
    const [userInfo, setUserInfo] = useState({});
    const [showExportModal, setShowExportModal] = useState(false);
    const [permissions, setPermissions] = useState({});
    const [generalSettings, setGeneralSettings] = useState({});
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000);

    const pageno = 1;
    const pagesize = 100;

    const isElectron = () => window && window.process && window.process.type;
    let ipcRenderer;
    if (isElectron()) {
        ipcRenderer = window.require('electron').ipcRenderer;
    }

    useEffect(() => {
        const fetchData = async () => {
            let userRolesData;
            let userinfo;
            let generalSetting;

            if (isElectron()) {
                userRolesData = await ipcRenderer.invoke('store-get', 'user_role');
                userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
                generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
                setUserInfo(userinfo);
            } else {
                initializeWebUserInfo();
                userRolesData = getWebUserInfo().user_role;
                generalSetting = getWebUserInfo().generalOpSettings;
                userinfo = getWebUserInfo().userInfo;
                setUserInfo(userinfo);
            }
            setPermissions(processPermissions(userRolesData));
            setGeneralSettings(generalSetting);
        };
        fetchData();
    }, []);

    const processPermissions = (roles) => {
        const permissionsMap = {};
        roles?.forEach?.((role) => {
            role.modules?.forEach?.((module) => {
                permissionsMap[module.moduleName] = module.permissions;
            });
        });
        return permissionsMap;
    };

    const hasPermission = (moduleName, action) => permissions[moduleName]?.[action] || false;

    const handleMobileChange = async (inputValue) => {
        if (inputValue.length === 3 || inputValue.length === 6 || inputValue.length === 9 || inputValue.length === 10) {
            await getFilterData(inputValue);
        } else if (inputValue.length === 0) {
            setPatientSuggestions([]);
        }
    };

    const getFilterData = async (inputValue) => {
        setPatientSearchLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}${GET_PATIENTS}`, {
                headers: {
                    PageNo: pageno,
                    PageSize: pagesize,
                    FilterText: inputValue,
                    XApiKey: process.env.REACT_APP_API_KEY,
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                const masterData = response.data.data?.masterData || [];
                setPatientSuggestions(masterData);
            }
        } catch (error) {
            console.error('Error filtering patients:', error);
            toast.error('Failed to search patients.', { autoClose: 1000 });
        } finally {
            setPatientSearchLoading(false);
        }
    };

    const patientOptions = useMemo(
        () =>
            patientSuggestions.map((patient) => ({
                value: patient.PatientID,
                label: `${patient.PatientName} - ${patient.Age} Y - ${patient.Month} M - ${patient.Days} D - ${patient.Place} - ${patient.MobileNo}`,
                ...patient,
            })),
        [patientSuggestions]
    );

    const clearReportData = useCallback(() => {
        setSalesData([]);
        setExpandedBills(new Set());
        setInvoiceDetails({});
        setLoadingDetails(new Set());
        setCurrentPage(1);
    }, []);

    const getSalesReport = useCallback(
        async (patientRow) => {
            const patient = patientRow ?? selectedPatient;
            const patientId = patient?.PatientID != null ? Number(patient.PatientID) : NaN;
            if (!patient || Number.isNaN(patientId) || patientId <= 0) {
                toast.error('Select a patient to load the report', { autoClose: 2000 });
                return;
            }

            setLoading(true);
            try {
                const from = fromDate ? new Date(fromDate) : null;
                const to = toDate ? new Date(toDate) : null;

                const formatDateParam = (date) => {
                    if (!date) return '';
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };

                const response = await axios.get(`${BASE_URL}${GET_SALES_REPORT}`, {
                    headers: {
                        XApiKey: process.env.REACT_APP_API_KEY,
                        Authorization: `Bearer ${token}`,
                    },
                    params: {
                        rpType: 'Patient',
                        fromDate: formatDateParam(from),
                        toDate: formatDateParam(to),
                        searchKey: patientId,
                        patientId: 0,
                        itemNo: 0,
                    },
                });

                if (response.status === 200) {
                    const data = response.data.data || [];
                    const sortedData = data.sort((a, b) => {
                        const invoiceA = a.InvoiceNo || '';
                        const invoiceB = b.InvoiceNo || '';
                        const numA = parseInt(invoiceA, 10) || 0;
                        const numB = parseInt(invoiceB, 10) || 0;
                        if (numA !== 0 || numB !== 0) {
                            return numB - numA;
                        }
                        return invoiceB.localeCompare(invoiceA);
                    });
                    setSalesData(sortedData);
                    setExpandedBills(new Set());
                    setInvoiceDetails({});
                    setLoadingDetails(new Set());
                    setCurrentPage(1);
                } else {
                    toast.error('Failed to fetch sales report', { autoClose: 1000 });
                }
            } catch (error) {
                console.error('Failed to fetch sales report:', error);
                toast.error('Failed to fetch sales report', { autoClose: 1000 });
            } finally {
                setLoading(false);
            }
        },
        [fromDate, toDate, selectedPatient, token]
    );

    const handlePatientSelect = (option) => {
        if (!option) {
            setSelectedPatient(null);
            clearReportData();
            return;
        }
        setSelectedPatient(option);
        getSalesReport(option);
    };

    const handleRunReport = () => {
        getSalesReport();
    };

    const handleReset = () => {
        setFromDate(formatLocalDate(new Date()));
        setToDate(formatLocalDate(new Date()));
        setSelectedPatient(null);
        setPatientSuggestions([]);
        clearReportData();
    };

    const handleExport = () => {
        if (salesData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const exportToExcel = () => {
        try {
            const excelData = salesData.map((row) => {
                const totalAmount = parseFloat(row.TotalAmount || 0);
                const discountAmount = parseFloat(row.DiscountAmount || 0);
                const grandTotal = totalAmount + discountAmount;
                return {
                    'Invoice No': formatInvoiceForPrint(row.InvoiceNo, row.Sequence),
                    Date: row.CreatedDate
                        ? new Date(row.CreatedDate).toLocaleDateString('en-GB')
                        : row.EntryDate
                            ? new Date(row.EntryDate).toLocaleDateString('en-GB')
                            : '',
                    'Patient Name': row.PatientName || '',
                    'Doctor Name': row.DoctorName || '-',
                    'Grand Total': Number(grandTotal.toFixed(2)),
                    'Tax Amount': Number(parseFloat(row.TaxAmount || 0).toFixed(2)),
                    Discount: Number(parseFloat(row.DiscountAmount || 0).toFixed(2)),
                    'Total Amount': Number(totalAmount.toFixed(2)),
                    'Balance Due': Number(parseFloat(row.BalanceDue || 0).toFixed(2)),
                };
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);
            ws['!cols'] = [
                { wch: 12 },
                { wch: 12 },
                { wch: 25 },
                { wch: 20 },
                { wch: 15 },
                { wch: 12 },
                { wch: 12 },
                { wch: 15 },
                { wch: 15 },
            ];
            XLSX.utils.book_append_sheet(wb, ws, 'Sales Patient Wise');

            const fromStr = fromDate ? new Date(fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toStr = toDate ? new Date(toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const patientSlug = (selectedPatient?.PatientName || 'patient').replace(/[^\w\-]+/g, '_');
            const filename = `Sales_Patient_Wise_${patientSlug}_${fromStr}_to_${toStr}.xlsx`;
            XLSX.writeFile(wb, filename);
            toast.success('Excel file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF('landscape', 'mm', 'a4');
            doc.setFontSize(16);
            doc.text('Sales Patient Wise Report', 14, 15);
            doc.setFontSize(10);
            const fromStr = fromDate ? new Date(fromDate).toLocaleDateString('en-GB') : 'All';
            const toStr = toDate ? new Date(toDate).toLocaleDateString('en-GB') : 'All';
            doc.text(`Date Range: ${fromStr} to ${toStr}`, 14, 22);
            if (selectedPatient?.PatientName) {
                doc.text(`Patient: ${selectedPatient.PatientName} (ID: ${selectedPatient.PatientID})`, 14, 28);
            }

            const tableData = salesData.map((row) => {
                const totalAmount = parseFloat(row.TotalAmount || 0);
                const discountAmount = parseFloat(row.DiscountAmount || 0);
                const grandTotal = totalAmount + discountAmount;
                const dateStr = row.CreatedDate
                    ? new Date(row.CreatedDate).toLocaleDateString('en-GB')
                    : row.EntryDate
                        ? new Date(row.EntryDate).toLocaleDateString('en-GB')
                        : '';
                return [
                    formatInvoiceForPrint(row.InvoiceNo, row.Sequence),
                    dateStr,
                    row.PatientName || '',
                    row.DoctorName || '-',
                    grandTotal.toFixed(2),
                    parseFloat(row.TaxAmount || 0).toFixed(2),
                    parseFloat(row.DiscountAmount || 0).toFixed(2),
                    totalAmount.toFixed(2),
                    parseFloat(row.BalanceDue || 0).toFixed(2),
                ];
            });

            autoTable(doc, {
                head: [
                    [
                        'Inv. No',
                        'Date',
                        'Patient Name',
                        'Doctor Name',
                        'G Total',
                        'Tax Amount',
                        'Discount',
                        'Total Amount',
                        'Balance Due',
                    ],
                ],
                body: tableData,
                startY: selectedPatient?.PatientName ? 32 : 28,
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                margin: { top: 28, left: 14, right: 14 },
            });

            const filename = `Sales_Patient_Wise_${fromStr.replace(/\//g, '-')}_to_${toStr.replace(/\//g, '-')}.pdf`;
            doc.save(filename);
            toast.success('PDF file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        }
    };

    const handleExportFormat = (format) => {
        if (format === 'excel') exportToExcel();
        else if (format === 'pdf') exportToPDF();
    };

    const totalPages = Math.ceil(salesData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = salesData.slice(startIndex, endIndex);

    const toggleBillExpansion = useCallback(
        async (row) => {
            const invoiceKey = `${row.Sequence}_${row.InvoiceNo}`;
            const isExpanded = expandedBills.has(invoiceKey);

            if (isExpanded) {
                setExpandedBills((prev) => {
                    const next = new Set(prev);
                    next.delete(invoiceKey);
                    return next;
                });
            } else {
                setExpandedBills((prev) => new Set(prev).add(invoiceKey));

                if (!invoiceDetails[invoiceKey]) {
                    setLoadingDetails((prev) => new Set(prev).add(invoiceKey));
                    try {
                        const response = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
                            headers: {
                                XApiKey: process.env.REACT_APP_API_KEY,
                                Authorization: `Bearer ${token}`,
                            },
                            params: {
                                Sequence: row.Sequence,
                                InvoiceNo: row.InvoiceNo.toString(),
                            },
                        });

                        if (response.status === 200) {
                            const invoiceData = response.data.data;
                            if (invoiceData && invoiceData.details) {
                                setInvoiceDetails((prev) => ({
                                    ...prev,
                                    [invoiceKey]: invoiceData,
                                }));
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching invoice details:', error);
                        toast.error('Failed to load invoice details', { autoClose: 2000 });
                    } finally {
                        setLoadingDetails((prev) => {
                            const next = new Set(prev);
                            next.delete(invoiceKey);
                            return next;
                        });
                    }
                }
            }
        },
        [expandedBills, invoiceDetails, token]
    );

    return (
        <div className="flex h-full bg-background">
            {!embedded && <LeftMenu />}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />

            {loading && <Spinner />}

            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormat}
            />

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Sales Reports
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">Sales Patient Wise</h1>
                                </div>
                            )}
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700 shrink-0 w-[9.5rem]">
                                    From Date
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                    />
                                </label>
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700 shrink-0 w-[9.5rem]">
                                    To Date
                                    <input
                                        type="date"
                                        value={toDate}
                                        min={fromDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                    />
                                </label>
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700 flex-1 min-w-0 w-full">
                                    <span className="flex items-center justify-between gap-2">
                                        Patient
                                        {patientSearchLoading && (
                                            <span className="text-xs font-normal text-indigo-500">Searching…</span>
                                        )}
                                    </span>
                                    <Select
                                        placeholder="Type mobile or name (min. 3 chars)..."
                                        inputId="sales-patient-wise-search"
                                        value={
                                            selectedPatient
                                                ? patientOptions.find(
                                                    (o) => o.value === selectedPatient.PatientID
                                                ) || {
                                                    value: selectedPatient.PatientID,
                                                    label: selectedPatient.PatientName,
                                                    ...selectedPatient,
                                                }
                                                : null
                                        }
                                        options={patientOptions}
                                        formatOptionLabel={customOptionLabel}
                                        onInputChange={(value, { action }) => {
                                            if (action === 'input-change') {
                                                handleMobileChange(value);
                                            }
                                        }}
                                        onChange={handlePatientSelect}
                                        isClearable
                                        noOptionsMessage={() => 'Type mobile number or name to search'}
                                        styles={customSelectStyles}
                                        className="w-full"
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                        menuPosition="fixed"
                                    />
                                </label>
                            </div>
                            {selectedPatient && (
                                <p className="mt-2 text-xs text-slate-600">
                                    Selected:{' '}
                                    <span className="font-semibold text-slate-800">
                                        {selectedPatient.PatientName}
                                    </span>{' '}
                                    <span className="text-slate-500">(ID: {selectedPatient.PatientID})</span>
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3 shrink-0">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={handleReset}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0 px-3 pb-3">
                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-5">
                                <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Bills
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">{salesData.length}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Amount
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹
                                        {salesData
                                            .reduce((sum, item) => sum + parseFloat(item.TotalAmount || 0), 0)
                                            .toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Discount
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹
                                        {salesData
                                            .reduce((sum, item) => sum + parseFloat(item.DiscountAmount || 0), 0)
                                            .toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Grand Total
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹
                                        {salesData
                                            .reduce(
                                                (sum, item) =>
                                                    sum +
                                                    parseFloat(item.TotalAmount || 0) +
                                                    parseFloat(item.DiscountAmount || 0),
                                                0
                                            )
                                            .toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Pending
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹
                                        {salesData
                                            .reduce((sum, item) => sum + parseFloat(item.BalanceDue || 0), 0)
                                            .toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </HighlightMetricsModal>

                        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing{' '}
                                        <span className="font-semibold text-slate-700">
                                            {salesData.length === 0 ? 0 : startIndex + 1}-
                                            {Math.min(endIndex, salesData.length)}
                                        </span>{' '}
                                        of <span className="font-semibold text-slate-700">{salesData.length}</span>{' '}
                                        bills
                                        {selectedPatient && (
                                            <span className="ml-2 text-slate-600">
                                                for {selectedPatient.PatientName}
                                            </span>
                                        )}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs text-slate-600">Items per page:</label>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                                        >
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                            <option value={1000}>1000</option>
                                        </select>
                                    </div>
                                </div>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>

                            {salesData.length > 0 ? (
                                <SalesReportBillsTable
                                    rows={paginatedData}
                                    startIndex={startIndex}
                                    expandedBills={expandedBills}
                                    invoiceDetails={invoiceDetails}
                                    loadingDetails={loadingDetails}
                                    onToggleExpand={toggleBillExpansion}
                                    formatInvoice={(row) => formatInvoiceForPrint(row.InvoiceNo, row.Sequence)}
                                    userInfo={userInfo}
                                    showUserName={false}
                                />
                            ) : (
                                <div className="text-center py-12">
                                    <FiFileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                                    <p className="text-slate-500 text-lg">
                                        {selectedPatient
                                            ? 'No bills found for this patient in the selected date range'
                                            : 'Select a patient to load pharmacy sales for the date range'}
                                    </p>
                                </div>
                            )}
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(SalesPatientWise);
